import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL, DEFAULT_PAGE_DESCRIPTION, DEFAULT_PAGE_TITLE } from "../../utilities/constants";
import { handleApiCall } from "../../utilities/helper";
import imgPlaceholder from "../../assets/defaultlook.png";
import ImageZoom from "../../components/common/ImageZoom";
import img from "../../assets/product/product-01.jpg"; // You might want to pass the actual look image if available, or fetch it
import PageMeta from "../../components/common/PageMeta";
import NormalizedProductModal from "../../components/common/NormalizedProductModal";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";

const OutfitDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const look = location.state?.look; // Get look data from navigation state
    const initialSelectedItemId = location.state?.selectedItemId;

    const [outfitData, setOutfitData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [similarItems, setSimilarItems] = useState(null);
    const [similarLoading, setSimilarLoading] = useState(false);

    // Normalized Data States
    const [normalizedData, setNormalizedData] = useState(null);
    const [isNormalizedModalOpen, setIsNormalizedModalOpen] = useState(false);
    const [fetchingNormalized, setFetchingNormalized] = useState(false);

    useEffect(() => {
        const fetchOutfitData = async () => {
            setLoading(true);
            try {
                const response = await handleApiCall(
                    `${API_BASE_URL}/api/1.0.0/aigenlook/v2/result/${id}`,
                    {
                        method: "GET",
                    }
                );
                if (response.responseStatus) {
                    setOutfitData(response.data);
                }
            } catch (e) {
                console.error("Error fetching outfit data:", e);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchOutfitData();
        }
    }, [id]);

    const imgs = useMemo(() =>
        outfitData?.items?.map((item) => ({
            id: item.id,
            url: item.itemImageUrl,
            type: item.itemType,
            name: item.itemName,
            color: item.color,
            material: item.material,
            verified: item.verified,
            baseItemId: item.baseItemId,
        })) || [], [outfitData]);

    const selectedItem = useMemo(() => imgs.find((img) => img.id === selectedId), [imgs, selectedId]);

    useEffect(() => {
        if (imgs.length) {
            if (initialSelectedItemId) {
                // If we have an initial selection from navigation, use it (but only once or if it matches an item)
                const foundItem = imgs.find(i => i.id === initialSelectedItemId);
                if (foundItem) {
                    setSelectedId(initialSelectedItemId);
                } else if (!selectedId) {
                    setSelectedId(imgs[0].id);
                }
            } else if (!selectedId) {
                setSelectedId(imgs[0].id);
            }
        }
    }, [imgs, selectedId, initialSelectedItemId]);

    // Auto-trigger similar products when selected item changes
    useEffect(() => {
        setSimilarItems(null);
        if (selectedId && selectedItem && selectedItem.baseItemId === null) {
            handleFindSimilar();
        }
    }, [selectedId]);


    const handleFindSimilar = async () => {
        if (!selectedItem) return;

        if (selectedItem.baseItemId === null) {
            setSimilarLoading(true);
            try {
                const response = await handleApiCall(
                    `${API_BASE_URL}/api/1.0.0/product-shopping/item/${selectedItem?.id}/internal?location=ae`,
                    {
                        method: "POST",
                    }
                );
                console.log("Find Similar Response:", response);
                if (response?.data && response.data.length > 0) {
                    setSimilarItems(response.data);
                } else {
                    setSimilarItems([]); // Explicitly set to empty array if no data
                }
            } catch (error) {
                console.error("Error finding similar items:", error);
                setSimilarItems([]); // Set to empty array on error
            } finally {
                setSimilarLoading(false);
            }
        } else {
            console.log("baseItemId is not null, skipping API call.");
        }
    };

    const Info = ({ label, value, labelClass = "", valueClass = "" }) => (
        <div>
            <p className={`text-xs text-gray-500 mb-1 ${labelClass}`}>{label}</p>
            <p className={`dark:text-white ${valueClass}`}>
                {value
                    ?.toString()
                    .replace(/[{}\[\]"']/g, "")
                    .trim()}
            </p>
        </div>
    );

    return (
        <div className=" dark:bg-gray-800 rounded-xl overflow-hidden">
            <PageMeta
                title={`Outfit Details - ${DEFAULT_PAGE_TITLE}`}
                description={`Look Generation - ${DEFAULT_PAGE_DESCRIPTION}`}
            />
            {/* Modal for Normalized Product */}
            <NormalizedProductModal
                isOpen={isNormalizedModalOpen}
                onClose={() => setIsNormalizedModalOpen(false)}
                data={normalizedData}
            />
            <div className="flex justify-between items-center mb-">
                <PageBreadcrumb
                    pageTitle=" Outfit Details"
                />
            </div>
            <div>
                <button
                    onClick={() => navigate(-1)}
                    className="mb-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white flex items-center gap-2"
                >
                    <span>&larr;</span> Back
                </button>

                <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden min-h-[calc(100vh-140px)] flex flex-col">
                    {!loading && outfitData && (
                        <div className="flex-1 p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 h-full">
                            {/* Column 1: Zoomable Outfit Image (Spans 4 columns) */}
                            <div className="lg:col-span-4 md:col-span-1 h-full min-h-[300px] lg:min-h-[500px] bg-gray-50 dark:bg-gray-900 rounded-2xl overflow-hidden flex items-center justify-center border border-gray-100 dark:border-gray-700 shadow-sm relative group">
                                <div className="absolute top-4 left-4 z-10 bg-white/80 dark:bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold shadow-sm">
                                    Full Look
                                </div>
                                <ImageZoom src={look?.imageUrl || img} alt="Outfit Look" />
                            </div>

                            {/* Column 2: Selected Item & Thumbnails (Spans 4 columns) */}
                            <div className="lg:col-span-4 md:col-span-1 flex flex-col gap-6 h-full">
                                {/* Selected Item Container */}
                                <div className="flex-1 bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 p-4 opacity-50 font-display text-4xl text-gray-100 dark:text-gray-800 font-bold select-none pointer-events-none">
                                        ITEM
                                    </div>
                                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 z-10">Selected Piece</h3>

                                    <div className="flex-1 flex items-center justify-center relative z-10 min-h-[250px]">
                                        {selectedItem ? (
                                            <img
                                                src={selectedItem.url}
                                                alt={selectedItem.name}
                                                className="max-h-[300px] w-auto object-contain drop-shadow-lg transition-transform duration-500 group-hover:scale-110"
                                            />
                                        ) : (
                                            <div className="text-gray-400 flex flex-col items-center gap-2">
                                                <span>Select an item below</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Thumbnails Grid */}
                                <div className="flex flex-col gap-3">
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">In this Look</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {imgs.map((img) => (
                                            <button
                                                key={img.id}
                                                onClick={() => setSelectedId(img.id)}
                                                className={`relative rounded-xl overflow-hidden border-2 transition-all duration-200 flex-shrink-0 w-16 h-16 aspect-square group
                                                    ${selectedId === img.id
                                                        ? "border-brand-600 dark:border-brand-400 shadow-md scale-105 ring-2 ring-brand-100 dark:ring-brand-900/30 w-18 h-18"
                                                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500 opacity-80 hover:opacity-100 hover:scale-105"
                                                    }`}
                                            >
                                                <img
                                                    src={img.url}
                                                    alt={img.name}
                                                    className="w-full h-full object-cover"
                                                />
                                                {selectedId === img.id && (
                                                    <div className="absolute inset-0 bg-brand-600/10 dark:bg-brand-400/10" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Column 3: Item Details & Similar Items (Spans 4 columns) */}
                            <div className="lg:col-span-4 md:col-span-2 lg:flex flex-col h-full">
                                {selectedItem && (
                                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 mb-6 relative overflow-hidden">
                                        {/* Background decoration */}
                                        <div className="absolute -right-6 -top-6 w-24 h-24 bg-brand-500/5 rounded-full blur-2xl pointer-events-none"></div>

                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                            <span className="w-1 h-4 bg-brand-500 rounded-full"></span>
                                            Product Details
                                        </h3>

                                        <div className="space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <Info
                                                    valueClass="font-medium capitalize text-gray-900 dark:text-white"
                                                    label="Type"
                                                    value={selectedItem.type}
                                                />
                                                <Info
                                                    valueClass="font-medium capitalize text-gray-900 dark:text-white"
                                                    label="Color"
                                                    value={selectedItem.color}
                                                />
                                                <Info
                                                    valueClass="font-medium capitalize text-gray-900 dark:text-white"
                                                    label="Item Name"
                                                    value={selectedItem.name}
                                                />
                                                <Info
                                                    valueClass="font-medium capitalize text-gray-900 dark:text-white"
                                                    label="Material"
                                                    value={selectedItem.material}
                                                />
                                                <div className="col-span-2 grid grid-cols-2 gap-4">
                                                    <p className="text-xs text-gray-500 mb-1">Status</p>
                                                    <span
                                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold w-fit
                                                            ${selectedItem.verified
                                                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800"
                                                                : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                                                            }`}
                                                    >
                                                        {selectedItem.verified ? "Verified" : "Unverified"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}


                                {/* Similar Items Grid */}
                                <div className="flex-1 min-h-[180px] flex flex-col">
                                   
                                    <div className="flex-1 overflow-y-auto pr-1 -mr-1">
                                        {similarItems && similarItems.length > 0 ? (
                                            <div className="grid grid-cols-2 gap-3 pb-2 max-h-[300px] overflow-y-auto">
                                                {similarItems.map((item) => (
                                                    <div
                                                        key={item.id}
                                                        className="block bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-3 hover:shadow-xl transition-all duration-300 text-left group h-full  flex-col relative"
                                                    >
                                                        <a
                                                            href={item.link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="block"
                                                        >
                                                            <div className="aspect-square w-full mb-3 bg-gray-50 dark:bg-gray-900 rounded-lg flex items-center justify-center overflow-hidden relative">
                                                                {item.thumbnail ? (
                                                                    <img
                                                                        src={item.thumbnail}
                                                                        alt={item.title}
                                                                        className="w-full h-full object-contain mix-blend-multiply dark:mix-blend-normal group-hover:scale-110 transition-transform duration-500"
                                                                    />
                                                                ) : (
                                                                    <span className="text-xs text-gray-400">
                                                                        No Image
                                                                    </span>
                                                                )}
                                                                {/* Price Tag Overlay */}
                                                                {item?.price && (
                                                                    <div className="absolute bottom-1 right-1 bg-black/70 backdrop-blur-md text-white text-[10px] px-1.5 py-0.5 rounded-md font-medium">
                                                                        {item?.price && item.price !== "N/A" ? item.price : "$ Click to know the price"}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <p
                                                                className="text-[10px] font-bold tracking-wide text-brand-600 dark:text-brand-400 uppercase mb-1"
                                                            >
                                                                {item?.source}
                                                            </p>
                                                            <Info
                                                                valueClass="text-xs font-medium capitalize text-gray-600 line-clamp-2"
                                                                value={selectedItem?.name}
                                                            />
                                                            <p
                                                                className="text-xs font-medium text-gray-900 dark:text-gray-100 line-clamp-2 leading-relaxed mb-auto"
                                                                title={item.title}
                                                            >
                                                                {item?.title}
                                                            </p>
                                                        </a>


                                                    </div>
                                                ))}
                                            </div>
                                        ) : similarItems?.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center h-48 text-center p-6 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-800/50">
                                                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-3 text-2xl">
                                                    😕
                                                </div>
                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                                                    No similar items found.
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
                                                    No data available for similar items.
                                                </p>
                                            </div>
                                        ) : (
                                            selectedItem?.baseItemId === null &&
                                            !similarLoading && (
                                                <div className="flex flex-col items-center justify-center h-48 text-center p-6 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 group hover:border-brand-200 transition-colors">
                                                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform text-2xl">
                                                        🔍
                                                    </div>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                                                        Discover Matches
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
                                                        Find similar items from online stores
                                                    </p>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OutfitDetails;
